import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, Box, Typography, IconButton, Tooltip, Slider, Stack, Chip, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.25;

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

/**
 * Visor de imágenes a resolución original (HD) para el módulo de imágenes.
 * Soporta zoom con rueda/controles, arrastre para desplazar, rotación,
 * inversión de escala de grises y navegación entre los archivos del estudio.
 */
export default function ImageViewerDialog({ open, onClose, files = [], initialIndex = 0 }) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [inverted, setInverted] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState(null);
  const [dragging, setDragging] = useState(false);

  const dragRef = useRef(null);

  const images = files.filter((f) => (f.fileType || '').startsWith('image/'));
  const current = images[index] || null;

  const resetView = useCallback(() => {
    setZoom(1); setRotation(0); setOffset({ x: 0, y: 0 }); setInverted(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const startAt = images.findIndex((f) => f.id === files[initialIndex]?.id);
    setIndex(startAt >= 0 ? startAt : 0);
    resetView();
  }, [open, initialIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = useCallback((next) => {
    if (images.length === 0) return;
    setIndex((prev) => (next + images.length) % images.length);
    resetView();
  }, [images.length, resetView]);

  // Atajos de teclado: flechas para navegar, +/- zoom, 0 reinicia
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goTo(index + 1);
      else if (e.key === 'ArrowLeft') goTo(index - 1);
      else if (e.key === '+' || e.key === '=') setZoom((z) => clampZoom(z + ZOOM_STEP));
      else if (e.key === '-') setZoom((z) => clampZoom(z - ZOOM_STEP));
      else if (e.key === '0') resetView();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, goTo, resetView]);

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom((z) => clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y };
    setDragging(true);
  };
  const handleMouseMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset({
      x: d.baseX + (e.clientX - d.startX),
      y: d.baseY + (e.clientY - d.startY),
    });
  };
  const endDrag = () => { dragRef.current = null; setDragging(false); };

  if (!current) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay imágenes disponibles para visualizar.
          </Typography>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ sx: { bgcolor: '#0B0F14' } }}>
      {/* Barra de herramientas */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
        bgcolor: '#111827', borderBottom: '1px solid #1F2937', flexWrap: 'wrap',
      }}>
        <Typography variant="subtitle2" sx={{ color: '#E5E7EB', fontWeight: 700, mr: 1 }}>
          {current.fileName || 'Imagen'}
        </Typography>
        <Chip size="small" label={`${index + 1} / ${images.length}`}
          sx={{ bgcolor: '#1F2937', color: '#9CA3AF' }} />
        {natural && (
          <Chip size="small" label={`${natural.w} × ${natural.h} px`}
            sx={{ bgcolor: '#1F2937', color: '#9CA3AF' }} />
        )}
        {current.fileSizeMB && (
          <Chip size="small" label={`${current.fileSizeMB} MB`}
            sx={{ bgcolor: '#1F2937', color: '#9CA3AF' }} />
        )}

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 240 }}>
          <Tooltip title="Alejar (-)">
            <IconButton size="small" sx={{ color: '#E5E7EB' }}
              onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Slider size="small" value={zoom} min={MIN_ZOOM} max={MAX_ZOOM} step={ZOOM_STEP}
            onChange={(_, v) => setZoom(v)} sx={{ color: '#2CB8A0', mx: 1 }} />
          <Tooltip title="Acercar (+)">
            <IconButton size="small" sx={{ color: '#E5E7EB' }}
              onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ color: '#9CA3AF', minWidth: 44, textAlign: 'right' }}>
            {Math.round(zoom * 100)}%
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem sx={{ borderColor: '#1F2937', mx: 1 }} />

        <Tooltip title="Rotar 90°">
          <IconButton size="small" sx={{ color: '#E5E7EB' }}
            onClick={() => setRotation((r) => (r + 90) % 360)}>
            <RotateRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Invertir grises">
          <IconButton size="small" sx={{ color: inverted ? '#2CB8A0' : '#E5E7EB' }}
            onClick={() => setInverted((v) => !v)}>
            <InvertColorsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Restablecer vista (0)">
          <IconButton size="small" sx={{ color: '#E5E7EB' }} onClick={resetView}>
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Abrir original en nueva pestaña">
          <IconButton size="small" sx={{ color: '#E5E7EB' }}
            onClick={() => window.open(current.downloadURL, '_blank', 'noopener')}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Descargar">
          <IconButton size="small" sx={{ color: '#E5E7EB' }} component="a"
            href={current.downloadURL} download={current.fileName} target="_blank" rel="noopener">
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cerrar (Esc)">
          <IconButton size="small" sx={{ color: '#E5E7EB' }} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Lienzo */}
      <Box
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onDoubleClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
        sx={{
          flex: 1, position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: dragging ? 'grabbing' : 'grab', bgcolor: '#0B0F14',
        }}
      >
        <img
          src={current.downloadURL}
          alt={current.fileName || 'Estudio de imagen'}
          onLoad={(e) => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
          draggable={false}
          style={{
            maxWidth: zoom <= 1 ? '100%' : 'none',
            maxHeight: zoom <= 1 ? '100%' : 'none',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: dragging ? 'none' : 'transform 0.12s ease-out',
            filter: inverted ? 'invert(1)' : 'none',
            imageRendering: 'auto',
            userSelect: 'none',
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton onClick={() => goTo(index - 1)}
              sx={{ position: 'absolute', left: 12, color: '#E5E7EB', bgcolor: 'rgba(17,24,39,0.7)', '&:hover': { bgcolor: '#1F2937' } }}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={() => goTo(index + 1)}
              sx={{ position: 'absolute', right: 12, color: '#E5E7EB', bgcolor: 'rgba(17,24,39,0.7)', '&:hover': { bgcolor: '#1F2937' } }}>
              <NavigateNextIcon />
            </IconButton>
          </>
        )}
      </Box>

      {/* Tira de miniaturas */}
      {images.length > 1 && (
        <Box sx={{
          display: 'flex', gap: 1, p: 1, bgcolor: '#111827',
          borderTop: '1px solid #1F2937', overflowX: 'auto',
        }}>
          {images.map((f, i) => (
            <Box key={f.id || i} onClick={() => goTo(i)}
              sx={{
                width: 72, height: 72, flexShrink: 0, borderRadius: 1, overflow: 'hidden',
                cursor: 'pointer', border: `2px solid ${i === index ? '#2CB8A0' : 'transparent'}`,
                opacity: i === index ? 1 : 0.6,
              }}>
              <img src={f.downloadURL} alt={f.fileName || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
}
